/**
 * @file HidChannelProxy.ts
 * @description HID 通道代理。它基于一条真实 HID 通道，为逻辑设备提供独立的通道视图。
 * 典型场景：设备不是系统直接扫描出来的 HID，但仍希望复用现有 HID 设备驱动和 feature。
 */

import type { ChannelInfo } from '../../Channel'
import type { HidChannel, HidReportListener } from '../HidChannel'
import type { TDevice } from '../../../types/device'

export type HidChannelProxyOptions = {
  /** 逻辑设备展示给上层的 VID/PID，不必等于父物理通道的 VID/PID。 */
  deviceInfo: ChannelInfo
  /** 少数场景需要代理关闭时连带关闭父通道；默认只关闭代理视图。 */
  closeParent?: boolean
}

export class HidChannelProxy implements HidChannel {
  private readonly listeners = new Set<HidReportListener>()

  /** 只依赖父 HID 通道和逻辑设备信息，避免代理层知道具体设备类型。 */
  constructor(
    private readonly parentChannel: HidChannel,
    private readonly options: HidChannelProxyOptions,
  ) {}

  /** 代理复用父物理通道的原生对象，便于底层调试仍能定位真实连接。 */
  public get nativeDevice(): TDevice {
    return this.parentChannel.nativeDevice
  }

  /** 代理是否可用由父物理通道决定。 */
  public get isConnected(): boolean {
    return this.parentChannel.isConnected
  }

  /** 对外暴露逻辑设备身份，避免子设备被误显示成父设备。 */
  public get deviceInfo(): ChannelInfo {
    return this.options.deviceInfo
  }

  /** 打开代理时只保证父 HID 通道可用，不额外触发系统扫描或授权。 */
  public async open(): Promise<void> {
    if (!this.parentChannel.isConnected) {
      await this.parentChannel.open()
    }
  }

  /** 默认只释放代理视图；是否关闭父通道由创建方通过 closeParent 明确决定。 */
  public async close(): Promise<void> {
    this.listeners.clear()
    if (this.options.closeParent) {
      await this.parentChannel.close()
    }
  }

  /** 代理的 output report 仍经由父物理 HID 通道发送。 */
  public sendOutputReport(reportId: number, data: Uint8Array): Promise<void> {
    return this.parentChannel.sendOutputReport(reportId, data)
  }

  /** 代理的 feature 写入仍经由父物理 HID 通道发送。 */
  public sendFeatureReport(reportId: number, data: Uint8Array): Promise<void> {
    return this.parentChannel.sendFeatureReport(reportId, data)
  }

  /** 代理的 feature 读取仍经由父物理 HID 通道返回。 */
  public getFeatureReport(reportId: number): Promise<DataView> {
    return this.parentChannel.getFeatureReport(reportId)
  }

  /** 逻辑设备监听自己的 input report，由创建方把过滤/转换后的数据注入进来。 */
  public onInputReport(callback: HidReportListener): void {
    this.listeners.add(callback)
  }

  /** 移除逻辑设备 input report 监听，避免逻辑设备销毁后继续收包。 */
  public offInputReport(callback: HidReportListener): void {
    this.listeners.delete(callback)
  }

  /** 注入已经归一到逻辑设备语义的 input report，不让逻辑设备感知父通道复用细节。 */
  public emitInputReport(reportId: number, data: Uint8Array): void {
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
    for (const listener of this.listeners) {
      listener(reportId, view)
    }
  }
}
